import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@oforce/global-components';
import { useDownloadFile } from '../../hooks';
import { useBulkDownloadDocumentsJobContext } from '../../context';
import { ToastContent } from '../../components/react-toastify';

export default function BulkDownloadDocumentsJob() {
  const { jobId, message, finished, filename, url, error, jobErrors, clearJobId } =
    useBulkDownloadDocumentsJobContext();

  const toastId = useRef(null);

  useEffect(() => {
    if (!toast.current && jobId) {
      toastId.current = toast(<BulkDownloadDocuments />, { autoClose: false, closeOnClick: false });
    }
  }, [toastId, jobId, clearJobId]);

  useEffect(() => {
    if (!toastId.current) {
      return;
    }

    if (error) {
      toast.update(toastId.current, {
        render: () => <ErrorBulkDownloadDocuments error={jobErrors || error} />,
        onClose: clearJobId
      });
    } else if (!finished && !url) {
      toast.update(toastId.current, {
        render: () => <BulkDownloadDocuments message={message} />
      });
    } else if (finished) {
      toast.update(toastId.current, {
        render: () => (
          <BulkDownloadDocumentsDownload
            message={message}
            url={url}
            filename={filename}
            errors={jobErrors}
            clearJobId={clearJobId}
          />
        ),
        onClose: clearJobId
      });
    }
    // eslint-disable-next-line
  }, [toastId, message, filename, url, finished, error, jobErrors]);

  return <></>;
}

const BulkDownloadDocuments = ({ message }) => {
  let title = message ?? 'Preparing Bulk Download Documents';

  return (
    <ToastContent
      title={title}
      icon={['far', 'spinner-third']}
      iconProps={{ spin: true }}
      message="Please allow us some time to gather the documents."
    />
  );
};

const ErrorBulkDownloadDocuments = ({ error }) => (
  <ToastContent
    title="Error Bulk Downloading Documents"
    titleProps={{ className: 'text-danger' }}
    icon={['fas', 'times-circle']}
    message={error.message || error}
  />
);

const BulkDownloadDocumentsDownload = ({ message, url, filename, errors, clearJobId }) => {
  const { download, loading } = useDownloadFile({ url, filename });

  const downloadAndClearJobId = () => {
    download();
    clearJobId();
    toast.dismiss();
  };

  return (
    <ToastContent
      title={message || 'Documents Ready'}
      titleProps={{ className: 'text-success' }}
      icon={url ? ['fas', 'check-circle'] : ['far', 'exclamation-triangle']}
      message={url && 'Your documents are ready for download.'}
    >
      {errors && errors.length > 0 && errors.map(e => <div key={e}>{e}</div>)}
      <div>
        {url && (
          <Button
            color="link"
            size="sm"
            icon={['far', 'arrow-to-bottom']}
            className="mr-2"
            loading={loading}
            loadingText="Downloading..."
            onClick={downloadAndClearJobId}
          >
            Download Now
          </Button>
        )}
      </div>
    </ToastContent>
  );
};
